// Anzeige-Helfer der Onpage-Analyse (SPEC 4.2): Scores, Veränderungen, Adressen. Rein, ohne Netzwerk.
import type { OnpageRow } from "./api/types";
import { formatCount } from "./format";

export const SCORE_TARGET_FALLBACK = 80;

/** Farbklasse eines Scores (Mockup: ab 70 grün, ab 55 blau, sonst orange). Der Wert steht immer als Zahl daneben. */
export function scoreClass(score: number): string {
  if (score >= 70) return "bg-success-bg text-success";
  if (score >= 55) return "bg-info-bg text-info";
  return "bg-warning-bg text-warning";
}

/** +3 → „+3“, −7 → „−7“ (echtes Minuszeichen), 0 → „±0“. */
export function deltaLabel(delta: number | null): string {
  if (delta === null) return "";
  if (delta === 0) return "±0";
  return `${delta > 0 ? "+" : "−"}${formatCount(Math.abs(delta))}`;
}

export function deltaClass(delta: number | null): string {
  if (delta === null || delta === 0) return "text-muted";
  return delta > 0 ? "text-success" : "text-danger";
}

export function aiLabel(cited: number | null, total: number): string {
  return cited === null ? "nicht ermittelt" : `${cited} von ${total} Plattformen`;
}

export function typeLabel(weekly: boolean): string {
  return weekly ? "Wochen-Check" : "Manuell";
}

export type TypeFilter = "all" | "weekly" | "manual";

export function matchesFilter(row: OnpageRow, filter: TypeFilter): boolean {
  return filter === "all" || (filter === "weekly" ? row.weekly : !row.weekly);
}

export function matchesQuery(row: OnpageRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  return `${row.title ?? ""} ${row.url} ${row.path}`.toLowerCase().includes(needle);
}

/** Welcher Snapshot sich beim „Öffnen“ zeigt: der laufende (Fortschritt), sonst der letzte brauchbare. */
export function openTarget(row: OnpageRow): number | null {
  if (row.latest && row.latest.state !== "failed") return row.latest.id;
  return row.last_done?.id ?? row.latest?.id ?? null;
}

// --- Adressen ---------------------------------------------------------------------------------

export const MAX_BATCH_FALLBACK = 200;

/** URLs aus Textfeld oder CSV: getrennt durch Zeilenumbruch, Komma, Semikolon oder Leerzeichen, ohne Doppelte. */
export function parseUrls(text: string): string[] {
  const found: string[] = [];
  for (const token of text.replace(/"/g, " ").split(/[\s,;]+/)) {
    if (token && !found.includes(token)) found.push(token);
  }
  return found;
}

function inDomain(host: string, domain: string): boolean {
  const base = domain.toLowerCase().replace(/^www\./, "");
  const name = host.toLowerCase().replace(/\.$/, "");
  return name === base || name.endsWith(`.${base}`);
}

/** Prüfung wie im Backend, damit Fehler vor dem Start sichtbar werden. Liefert je Fehler einen Satz. */
export function validateUrls(urls: string[], domain: string): string[] {
  const errors: string[] = [];
  for (const raw of urls) {
    let parsed: URL | null = null;
    try {
      parsed = /^https?:\/\//i.test(raw) ? new URL(raw) : null;
    } catch {
      parsed = null;
    }
    if (!parsed) {
      errors.push(`„${raw.slice(0, 80)}“ ist keine vollständige URL (https://…)`);
    } else if (!inDomain(parsed.hostname, domain)) {
      errors.push(`${parsed.hostname} gehört nicht zur Projekt-Domain ${domain}`);
    } else if (parsed.port && parsed.port !== "80" && parsed.port !== "443") {
      errors.push(`${parsed.hostname}: Ports außer 80 und 443 sind nicht erlaubt`);
    }
  }
  return errors;
}
